import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Something new: the brief. For a one-shot the writer drafts the whole story;
// for a series it drafts chapter 1 and keeps the rest of the plan for later.
// Either way the editor checks the draft and fixes it, and after that you
// just talk to the writer in the room.

const LENGTHS = [1000, 2500, 4000, 6000, 10000];
const TONES = ['slow burn', 'yearning', 'fluff', 'angst', 'funny', 'dark', 'explicit'];
const POVS = ['third person, close', 'third person, shifting', 'first person', 'second person'];
const TENSES = ['past', 'present'];

const PlusIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14" /><path d="M5 12h14" />
  </svg>
);
const XIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12" /><path d="M18 6L6 18" />
  </svg>
);
const UpIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 15l6-6 6 6" />
  </svg>
);

export default function WritingNew() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [models, setModels] = useState([]);
  const [b, setB] = useState({
    title: '', world: '', kind: 'oneshot', characters: '', premise: '',
    beats: ['', ''], keep: '', avoid: '', words: 4000, pov: POVS[0], tense: 'past',
    tones: [], style: '', writer_model: '', editor_model: '', arc: '', chapters: 5,
  });
  const [customLen, setCustomLen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    axios.get(`${API}/translation-models?token=${token}`)
      .then(res => {
        const list = res.data.models || [];
        setModels(list);
        const last = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
        const ok = (id) => id && list.some(m => m.id === id);
        setB(prev => ({
          ...prev,
          writer_model: ok(last('writerModel')) ? last('writerModel') : (list[0]?.id || ''),
          editor_model: ok(last('editorModel')) ? last('editorModel') : (list[1]?.id || list[0]?.id || ''),
        }));
      })
      .catch(() => {});
  }, []);

  const set = (patch) => setB(prev => ({ ...prev, ...patch }));
  const setBeat = (i, v) => set({ beats: b.beats.map((x, k) => (k === i ? v : x)) });
  const moveBeat = (i) => {
    if (i === 0) return;
    const next = [...b.beats];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    set({ beats: next });
  };
  const toggleTone = (t) => set({ tones: b.tones.includes(t) ? b.tones.filter(x => x !== t) : [...b.tones, t] });

  const beats = b.beats.map(x => x.trim()).filter(Boolean);
  const perBeat = beats.length ? Math.round(b.words / beats.length / 50) * 50 : 0;
  const series = b.kind === 'series';
  const ready = b.title.trim() && b.premise.trim() && beats.length > 0 && b.writer_model && b.editor_model && b.words >= 300 && (!series || b.chapters >= 2);

  const start = () => {
    if (!ready || busy) return;
    setBusy(true);
    setErrorMsg('');
    try { localStorage.setItem('writerModel', b.writer_model); localStorage.setItem('editorModel', b.editor_model); } catch (e) { /* fine */ }
    axios.post(`${API}/writing/works`, {
      token,
      title: b.title.trim(), world: b.world.trim(), kind: b.kind, characters: b.characters.trim(),
      premise: b.premise.trim(), beats, keep: b.keep.trim(), avoid: b.avoid.trim(),
      target_words: b.words, pov: b.pov, tense: b.tense, tones: b.tones, style_sample: b.style.trim(),
      writer_model: b.writer_model, editor_model: b.editor_model,
      arc: series ? b.arc.trim() : '', chapters_planned: series ? b.chapters : 1,
    })
      .then(res => navigate(series ? `/writing/book/${res.data.work.id}` : `/writing/p/${res.data.piece_id}`))
      .catch(err => { setErrorMsg(err?.response?.data?.detail || "couldn't start it"); setBusy(false); });
  };

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{`
        .wn-in:focus { border-color: rgba(201,147,90,0.5) !important; }
        .wn-btn:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
      `}</style>

      <header style={s.header}>
        <button className="wn-btn" style={s.back} onClick={() => navigate('/writing')} aria-label="back">←</button>
        <div style={s.title}>{series ? 'a new series' : 'a new story'}</div>
      </header>

      <main style={s.main}>
        <section style={s.section}>
          <label style={s.label} htmlFor="wn-title">title</label>
          <input id="wn-title" className="wn-in" style={s.titleInput} value={b.title} onChange={e => set({ title: e.target.value })} placeholder="what's it called?" />

          <div style={s.two}>
            <div style={s.field}>
              <label style={s.label} htmlFor="wn-world">fandom or world</label>
              <input id="wn-world" className="wn-in" style={s.input} value={b.world} onChange={e => set({ world: e.target.value })} placeholder="leave empty for an original" />
            </div>
            <div style={s.field}>
              <span style={s.label}>kind</span>
              <div style={s.seg}>
                <button className="wn-btn" style={s.segBtn(b.kind === 'oneshot')} onClick={() => set({ kind: 'oneshot' })}>one-shot</button>
                <button className="wn-btn" style={s.segBtn(series)} onClick={() => set({ kind: 'series' })}>series</button>
              </div>
            </div>
          </div>

          <label style={s.label} htmlFor="wn-chars">characters</label>
          <textarea id="wn-chars" className="wn-in" style={s.textarea} rows={3} value={b.characters} onChange={e => set({ characters: e.target.value })}
            placeholder={'one per line — name, pronouns, anything it must get right\ne.g. Mara — she/her — scar on her left hand, never swears'} />

          <label style={s.label} htmlFor="wn-premise">premise</label>
          <textarea id="wn-premise" className="wn-in" style={{ ...s.textarea, ...s.prose }} rows={4} value={b.premise} onChange={e => set({ premise: e.target.value })}
            placeholder="the situation, and what's at stake between them" />

          {series && (
            <>
              <label style={s.label} htmlFor="wn-arc">where the series is going</label>
              <textarea id="wn-arc" className="wn-in" style={{ ...s.textarea, ...s.prose }} rows={3} value={b.arc} onChange={e => set({ arc: e.target.value })}
                placeholder="the big arc, and how you want it to end — the writer plans the later chapters from this" />
              <div style={s.rowBetween}>
                <span style={s.label}>chapters planned</span>
                <span style={s.bigNum}>{b.chapters}</span>
              </div>
              <div style={s.chipRow}>
                {[3, 5, 10, 20].map(n => <button key={n} className="wn-btn" style={s.chip(b.chapters === n)} onClick={() => set({ chapters: n })}>{n}</button>)}
                <input className="wn-in" style={{ ...s.input, width: '90px' }} inputMode="numeric" value={b.chapters} aria-label="chapters planned"
                  onChange={e => set({ chapters: Math.min(100, Number(e.target.value.replace(/\D/g, '')) || 0) })} />
              </div>
              <div style={s.hint}>just a plan — you can add more chapters later, or stop early.</div>
            </>
          )}
        </section>

        <section style={s.section}>
          <div style={s.rowBetween}>
            <span style={s.label}>{series ? 'what happens in chapter 1, in order' : 'what happens, in order'}</span>
            {perBeat > 0 && <span style={s.hint}>about {perBeat.toLocaleString()} words each</span>}
          </div>
          {b.beats.map((beat, i) => (
            <div key={i} style={s.beatRow}>
              <span style={s.beatNum}>{i + 1}</span>
              <input className="wn-in" style={{ ...s.input, flex: 1 }} value={beat} onChange={e => setBeat(i, e.target.value)}
                placeholder={i === 0 ? 'e.g. she arrives soaked, letters in a tin' : 'and then…'} aria-label={`beat ${i + 1}`} />
              <button className="wn-btn" style={s.iconBtn} onClick={() => moveBeat(i)} disabled={i === 0} aria-label="move up"><UpIcon /></button>
              <button className="wn-btn" style={s.iconBtn} onClick={() => set({ beats: b.beats.filter((_, k) => k !== i) })} disabled={b.beats.length === 1} aria-label="remove beat"><XIcon /></button>
            </div>
          ))}
          <button className="wn-btn" style={s.addBtn} onClick={() => set({ beats: [...b.beats, ''] })}><PlusIcon /> add a beat</button>

          <div style={s.two}>
            <div style={s.field}>
              <label style={s.label} htmlFor="wn-keep">must keep</label>
              <textarea id="wn-keep" className="wn-in" style={s.textarea} rows={2} value={b.keep} onChange={e => set({ keep: e.target.value })} placeholder="canon facts, a line you want in it" />
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor="wn-avoid">must avoid</label>
              <textarea id="wn-avoid" className="wn-in" style={s.textarea} rows={2} value={b.avoid} onChange={e => set({ avoid: e.target.value })} placeholder="things that shouldn't happen or be said" />
            </div>
          </div>
        </section>

        <section style={s.section}>
          <div style={s.rowBetween}>
            <span style={s.label}>{series ? 'length of each chapter' : 'length'}</span>
            <span style={s.bigNum}>{b.words.toLocaleString()} words</span>
          </div>
          <div style={s.chipRow}>
            {LENGTHS.map(n => (
              <button key={n} className="wn-btn" style={s.chip(!customLen && b.words === n)} onClick={() => { setCustomLen(false); set({ words: n }); }}>
                {n >= 1000 ? `${n / 1000}k` : n}
              </button>
            ))}
            <button className="wn-btn" style={s.chip(customLen)} onClick={() => setCustomLen(true)}>custom</button>
            {customLen && (
              <input className="wn-in" style={{ ...s.input, width: '110px' }} inputMode="numeric" value={b.words}
                onChange={e => set({ words: Math.min(30000, Number(e.target.value.replace(/\D/g, '')) || 0) })} aria-label="words" />
            )}
          </div>
          {b.words > 10000 && <div style={s.hint}>long one — it's written in more scenes, and the editor reads it in parts.</div>}

          <div style={s.two}>
            <div style={s.field}>
              <label style={s.label} htmlFor="wn-pov">point of view</label>
              <select id="wn-pov" className="wn-in" style={s.input} value={b.pov} onChange={e => set({ pov: e.target.value })}>
                {POVS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor="wn-tense">tense</label>
              <select id="wn-tense" className="wn-in" style={s.input} value={b.tense} onChange={e => set({ tense: e.target.value })}>
                {TENSES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <span style={s.label}>tone</span>
          <div style={s.chipRow}>
            {TONES.map(t => <button key={t} className="wn-btn" style={s.chip(b.tones.includes(t))} onClick={() => toggleTone(t)}>{t}</button>)}
          </div>

          <label style={s.label} htmlFor="wn-style">write like this (optional)</label>
          <textarea id="wn-style" className="wn-in" style={{ ...s.textarea, ...s.prose }} rows={3} value={b.style} onChange={e => set({ style: e.target.value })}
            placeholder="paste a paragraph whose voice you want it to match" />
        </section>

        <section style={s.section}>
          <div style={s.two}>
            <div style={s.field}>
              <label style={s.label} htmlFor="wn-wm">writer</label>
              <select id="wn-wm" className="wn-in" style={s.input} value={b.writer_model} onChange={e => set({ writer_model: e.target.value })}>
                {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor="wn-em">editor</label>
              <select id="wn-em" className="wn-in" style={s.input} value={b.editor_model} onChange={e => set({ editor_model: e.target.value })}>
                {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div style={s.hint}>
            the editor checks every draft and every change against your brief and the story bible — names, facts,
            timeline, anything that contradicts itself — and takes out AI-isms. a different model from the writer usually catches more.
          </div>
        </section>

        {errorMsg && <div style={s.error}>{errorMsg}</div>}
        <button className="wn-btn" style={ready && !busy ? s.startBtn : s.startBtnOff} disabled={!ready || busy} onClick={start}>
          {busy ? 'starting…' : series ? 'start with chapter 1' : 'start writing'}
        </button>
        {!ready && <div style={{ ...s.hint, textAlign: 'center' }}>needs a title, a premise and at least one beat</div>}
      </main>
    </div>
  );
}

const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: {
    position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)',
  },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  main: { maxWidth: '680px', margin: '0 auto', padding: '22px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '16px' },
  section: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '20px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' },
  label: { fontSize: '10px', color: '#8a7466', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '4px' },
  hint: { fontSize: '12px', color: '#7a6a5a', lineHeight: 1.6 },
  field: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 },
  two: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' },
  rowBetween: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' },
  titleInput: { background: 'transparent', border: 'none', borderBottom: '1px solid rgba(201,147,90,0.25)', padding: '4px 0 10px', color: '#e8ddd0', fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', outline: 'none' },
  input: { width: '100%', boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '12px', padding: '11px 13px', color: '#e8ddd0', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', colorScheme: 'dark' },
  textarea: { width: '100%', boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '12px', padding: '11px 13px', color: '#e8ddd0', fontSize: '14px', lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif", outline: 'none', resize: 'vertical' },
  prose: { fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', lineHeight: 1.6 },
  seg: { display: 'flex', gap: '6px' },
  segBtn: (on) => ({ flex: 1, minHeight: '42px', borderRadius: '12px', border: on ? 'none' : '1px solid rgba(201,147,90,0.15)', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9a57a', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }),
  beatRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  beatNum: { width: '22px', textAlign: 'center', fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: '#c9935a', flexShrink: 0 },
  iconBtn: { width: '34px', height: '34px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.12)', background: 'transparent', color: '#8a7466', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 },
  addBtn: { alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '12px', border: '1px dashed rgba(201,147,90,0.25)', background: 'transparent', color: '#c9935a', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", marginLeft: '30px' },
  bigNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: '#c9935a' },
  chipRow: { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' },
  chip: (on) => ({ padding: '7px 14px', borderRadius: '16px', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", border: on ? 'none' : '1px solid rgba(201,147,90,0.18)', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9a57a' }),
  checkRow: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#c9b8a6', cursor: 'pointer', minHeight: '40px' },
  startBtn: { padding: '15px', borderRadius: '16px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '15px', fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", boxShadow: '0 6px 18px rgba(201,147,90,0.25)' },
  startBtnOff: { padding: '15px', borderRadius: '16px', border: 'none', background: 'rgba(201,147,90,0.15)', color: 'rgba(201,147,90,0.45)', fontSize: '15px', fontFamily: "'DM Sans', sans-serif" },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0' },
};
